import { DialogRef } from "@/types/common";
import { useQzTray } from "@/utils/qz";
import {
  ExclamationCircleFilled,
  LoadingOutlined,
  PrinterOutlined,
  SettingFilled,
} from "@ant-design/icons";
import { Alert, Button, Modal } from "antd";
import classNames from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Indicator from "../shared/Indicator";
import { useGlobalContext } from "@/hooks/context";
import { useLocalStorageState } from "@/utils/cache";

interface PrinterConfigProps {
  className?: string;
}

interface PrinterConfigModalProps {
  className?: string;
  qzError: unknown;
  loading: boolean;
  printers: string[];
  currentSelected: string | null;
  changeSelectedPrinter: (printer: string) => void;
  refreshPrinters: () => Promise<string[] | undefined>;
}

export interface PrinterRef {
  printLabel: (blob: Blob) => Promise<void>;
}

const PrinterConfig = forwardRef<PrinterRef, PrinterConfigProps>(
  (props, ref) => {
    const { className } = props;
    const { hintPrompt } = useGlobalContext();
    const printerConfigModalRef = useRef<DialogRef>(null);
    const [selectedPrinter, setSelectedPrinter] = useLocalStorageState<
      string | null
    >("wms-selected-printer", null);

    const {
      connect,
      disconnect,
      printers,
      refreshPrinters,
      printBlob,
      error: printerError,
      loading: printerLoading,
    } = useQzTray();

    const printLabel = useCallback(
      async (blob: Blob) => {
        if (selectedPrinter == null) {
          hintPrompt(
            "error",
            "No printer selected",
            "Please select a printer first",
          );
          return;
        }
        if (printerError != null) {
          hintPrompt(
            "error",
            "Printer error",
            "Please check the printer connection",
          );
          return;
        }
        try {
          return await printBlob(selectedPrinter, blob);
        } catch (error: any) {
          hintPrompt(
            "error",
            "Failed to print label",
            error.message ?? "Unknown error",
          );
          return;
        }
      },
      [selectedPrinter, hintPrompt, printerError, printBlob],
    );

    const knownPrinter = useMemo(() => {
      return printers.includes(selectedPrinter ?? "NO");
    }, [printers, selectedPrinter]);

    const icon = useMemo(() => {
      if (printerLoading) return <LoadingOutlined />;
      if (printerError) return <ExclamationCircleFilled />;
      if (!knownPrinter) return <ExclamationCircleFilled />;
      return <SettingFilled />;
    }, [printerLoading, printerError, knownPrinter]);

    const buttonColor = useMemo(() => {
      if (printerLoading) return "blue";
      if (printerError) return "danger";
      if (!knownPrinter) return "orange";
      return "primary";
    }, [printerError, printerLoading, knownPrinter]);

    const buttonText = useMemo(() => {
      if (printerLoading) return "Configuring Printers...";
      if (printerError) return "Printer Error";
      if (selectedPrinter) {
        if (knownPrinter) {
          return `Printer: ${selectedPrinter}`;
        } else {
          return "Invalid Printer: " + selectedPrinter;
        }
      } else {
        return "No Printer selected";
      }
    }, [selectedPrinter, printerLoading, printerError, knownPrinter]);

    useImperativeHandle(ref, () => ({
      printLabel: printLabel,
    }));

    useEffect(() => {
      if (printerError) {
        console.error("printerError", printerError);
      }
    }, [printerError]);

    useEffect(() => {
      connect()
        .then(refreshPrinters)
        .catch((error) => {
          console.error("error", error);
        });
      const saved = disconnect;
      return () => {
        saved()
          .then(() => {
            console.info("disconnected");
          })
          .catch((error) => {
            console.error("error", error);
          });
      };
    }, [connect, disconnect, refreshPrinters]);

    useEffect(() => {
      refreshPrinters();
    }, [refreshPrinters]);

    return (
      <div>
        <Button
          className={className}
          icon={icon}
          color={buttonColor}
          variant="solid"
          loading={printerLoading}
          onClick={() => {
            printerConfigModalRef.current?.showModal();
          }}
        >
          {buttonText}
        </Button>
        <PrinterConfigModal
          loading={printerLoading}
          ref={printerConfigModalRef}
          qzError={printerError}
          printers={printers}
          refreshPrinters={refreshPrinters}
          currentSelected={selectedPrinter}
          changeSelectedPrinter={setSelectedPrinter}
        />
      </div>
    );
  },
);

const PrinterConfigModal = forwardRef<DialogRef, PrinterConfigModalProps>(
  (props, ref) => {
    const {
      className,
      qzError,
      printers,
      loading,
      currentSelected,
      refreshPrinters,
      changeSelectedPrinter,
    } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const printerList = useMemo(() => {
      return printers;
    }, [printers]);

    const closeModal = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    const openModal = useCallback(() => {
      setIsModalOpen(true);
      refreshPrinters();
    }, [refreshPrinters]);

    useImperativeHandle(ref, () => ({
      showModal: openModal,
      hideModal: closeModal,
    }));

    return (
      <Modal
        title={loading ? "Loading Printers..." : "Printer Configuration"}
        className={className}
        open={isModalOpen}
        onCancel={closeModal}
        loading={loading}
        footer={null}
      >
        <div className="flex flex-col gap-2">
          {qzError != null && (
            <div>
              <Alert message="Error" type="error" />
              {JSON.stringify(qzError)}
            </div>
          )}
          <div className="">
            {printerList.length > 0 ? (
              <div>
                <div>
                  <Indicator label="Current Selected Printer" />
                  <div className="my-1">
                    <span
                      className={classNames(
                        "border-1 border-gray-200 rounded-md py-2",
                        "px-4 hover:bg-gray-100/50 cursor-pointer",
                      )}
                    >
                      {currentSelected ?? "No Printer Selected"}
                    </span>
                  </div>
                </div>

                <Indicator
                  label="Available Printers"
                  hint="scroll vertically to view all printers"
                />
                <div
                  className={classNames(
                    "flex flex-col border-1 border-gray-200",
                    "rounded-md max-h-[300px] overflow-y-auto",
                    "global__no-scrollbar",
                  )}
                >
                  {printerList.map((printer, index) => (
                    <div
                      key={index}
                      className={classNames(
                        "flex flex-row gap-2 items-center justify-between",
                        "px-4 py-2",
                        index === printerList.length - 1
                          ? "border-b-0"
                          : "border-b-[1px] border-gray-200",
                      )}
                    >
                      <div className="flex flex-row gap-2 items-center">
                        <PrinterOutlined />
                        <span>{printer}</span>
                      </div>
                      <Button
                        disabled={printer === currentSelected}
                        onClick={() => changeSelectedPrinter(printer)}
                        variant="outlined"
                      >
                        Select Printer
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <Alert showIcon message="No printers found" type="warning" />
              </div>
            )}
          </div>
          <div className="flex flex-row gap-2 justify-end pt-2">
            <Button type="primary" onClick={refreshPrinters} loading={loading}>
              Refresh Printers
            </Button>
            <Button onClick={closeModal}>Close</Button>
          </div>
        </div>
      </Modal>
    );
  },
);

export default PrinterConfig;
